import type { SandboxEvent, ScoredEvent } from "../hooks/useSandboxPersonas";
import type { SandboxEntity, SandboxEntityType } from "../types/entities";
import type { Interaction, InteractionType } from "../types/interactions";
import { VectorTerm } from "../types/vectorTerm";

const dotProduct = (a: number[], b: number[]): number => {
  let sum = 0;
  for (let i = 0; i < a.length; i++) {
    sum += a[i] * b[i];
  }
  return sum;
};

const norm = (a: number[]): number => {
  return Math.sqrt(dotProduct(a, a));
};

export function cosineSimilarity(a: number[], b: number[]): number {
  const na = norm(a);
  const nb = norm(b);
  if (na === 0 || nb === 0) return 0;
  return dotProduct(a, b) / (na * nb);
}

/**
 * Compute a user embedding as a weighted average of interaction entity embeddings.
 * Weight for each interaction = interactionWeight * decayWeight.
 */
const calculateUserEmbedding = (terms: VectorTerm[]): number[] => {
  if (terms.length === 0) return [];

  const dim = terms[0].interaction.entity.embedding!.length;
  const result = new Array<number>(dim).fill(0);

  for (const term of terms) {
    const interaction = term.interaction;
    const weight = term.interactionWeight * term.decayWeight;
    if (weight === 0) continue;

    for (let i = 0; i < dim; i++) {
      result[i] += interaction.entity.embedding![i] * weight;
    }
  }

  // Normalize to unit vector so the result can be used directly as a query vector
  const magnitude = norm(result);
  if (magnitude === 0) return result;
  for (let i = 0; i < dim; i++) {
    result[i] /= magnitude;
  }
  return result;
};

// entityContribution: dot(userEmb, entityEmb) / norm(userEmb)^2
// gives a projection-based contribution score for contributors display
function entityContribution(
  userEmbedding: number[],
  entityEmbedding: number[],
): number {
  const userNormSq = dotProduct(userEmbedding, userEmbedding);
  if (userNormSq === 0) return 0;
  return dotProduct(userEmbedding, entityEmbedding) / userNormSq;
}

/**
 * Score events against the user embedding.
 * Uses actual entity embeddings from interactions when available.
 */
export function scoreEventsWithEntityEmbeddings(
  userEmbedding: number[],
  events: SandboxEvent[],
  interactions: Interaction[],
  getWeight: (
    entityType: SandboxEntityType,
    interactionType: InteractionType,
  ) => number,
  getDecayMultiplier: (interaction: Interaction) => number = () => 1.0,
): ScoredEvent[] {
  // Build a map: entityId → { embedding, effective weight shown in interaction list }
  const entityMap = new Map<
    string,
    { embedding: number[]; displayWeight: number }
  >();
  for (const interaction of interactions) {
    const w = getWeight(interaction.entityType, interaction.interactionType);
    entityMap.set(interaction.entity.id, {
      embedding: interaction.entity.embedding,
      displayWeight: Math.round(w * getDecayMultiplier(interaction) * 10) / 10,
    });
  }

  const scored: ScoredEvent[] = events.filter((e) => e.embedding != null).map((event) => {
    const score = cosineSimilarity(userEmbedding, event.embedding);

    type RawContributor = {
      id: string;
      name: string;
      entityType: SandboxEntityType;
      contribution: number;
      interactionWeight?: number;
    };

    const candidates: RawContributor[] = [];

    const addEntity = (
      entity: SandboxEntity,
      entityType: SandboxEntityType,
    ) => {
      const fromInteraction = entityMap.get(entity.id);
      const emb = fromInteraction?.embedding ?? entity.embedding;
      const contribution = entityContribution(userEmbedding, emb);
      candidates.push({
        id: entity.id,
        name: entity.name,
        entityType,
        contribution,
        interactionWeight: fromInteraction?.displayWeight,
      });
    };

    for (const artist of event.artists) addEntity(artist, "artist");
    for (const genre of event.genres) addEntity(genre, "genre");
    addEntity(event.venue, "venue");

    // Prioritise entities that are in the interaction list, then by contribution
    candidates.sort((a, b) => {
      const aHas = a.interactionWeight !== undefined ? 1 : 0;
      const bHas = b.interactionWeight !== undefined ? 1 : 0;
      if (aHas !== bHas) return bHas - aHas;
      return b.contribution - a.contribution;
    });

    const contributors = candidates
      .slice(0, 3)
      .map(({ name, entityType, interactionWeight }) => ({
        name,
        entityType,
        interactionWeight,
      }));

    return { ...event, score, contributors };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored;
}

export function findNearestEntities(
  userEmbedding: number[],
  entities: Array<SandboxEntity & { entityType: SandboxEntityType }>,
  topN = 5,
): Array<
  SandboxEntity & { entityType: SandboxEntityType; similarity: number }
> {
  const scored = entities
    .filter((e) => e.embedding != null)
    .map((e) => ({
      ...e,
      similarity: cosineSimilarity(userEmbedding, e.embedding!),
    }));
  scored.sort((a, b) => b.similarity - a.similarity);
  return scored.slice(0, topN);
}

export { calculateUserEmbedding };
